/* Copyright (c) 2023-2026 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { If, Then, Else } from "react-if";
import { Link } from "react-router-dom";
import { FlexContainer, theme } from "@nordcloud/gnui";
import { isNotNil } from "~/tools";
import { ValueTag, LegendItemWrap } from "../styles";
import { StatusChartItem } from "../types";

type Props = {
  items: StatusChartItem[];
  direction?: "row" | "column";
  itemsPerColumn?: number;
};

export function ChartLegend({
  items,
  direction = "column",
  itemsPerColumn,
}: Props) {
  const alignSelf = direction === "row" ? "flex-start" : "center";

  const renderLegendItem = ({ label, color, count, link }: StatusChartItem) => (
    <LegendItemWrap key={label}>
      <ValueTag color={color}>{count}</ValueTag>
      <If condition={isNotNil(link)}>
        <Then>
          <Link to={link ?? ""}>{label}</Link>
        </Then>
        <Else>{label}</Else>
      </If>
    </LegendItemWrap>
  );

  const shouldSplitIntoColumns =
    direction === "column" && isNotNil(itemsPerColumn) && itemsPerColumn > 0;

  if (!shouldSplitIntoColumns) {
    return (
      <FlexContainer
        direction={direction}
        alignItems="flex-start"
        gap={theme.spacing.spacing05}
        css={{ alignSelf }}
      >
        {items.map(renderLegendItem)}
      </FlexContainer>
    );
  }

  const columnSize = Math.floor(itemsPerColumn);
  const columnCount = columnSize > 0 ? Math.ceil(items.length / columnSize) : 0;

  const columns: StatusChartItem[][] = Array.from(
    { length: columnCount },
    (_, columnIndex) => {
      const start = columnIndex * columnSize;
      return items.slice(start, start + columnSize);
    }
  );

  return (
    <FlexContainer
      direction="row"
      alignItems="flex-start"
      gap={theme.spacing.spacing07}
      css={{ alignSelf }}
    >
      {columns.map((columnItems, columnIndex) => (
        <FlexContainer
          key={`legend-column-${columnIndex}`}
          direction="column"
          alignItems="flex-start"
          gap={theme.spacing.spacing05}
        >
          {columnItems.map(renderLegendItem)}
        </FlexContainer>
      ))}
    </FlexContainer>
  );
}
